/**
 * Item Display Helpers for UI-Model-1
 *
 * Shared utility functions for displaying item icons, names, and type labels.
 * Used by ConfigPanel and ContainerContentsList.
 */

import React from 'react';
import { ItemAPI, isFieldAPI, isWidgetAPI, isCategoryAPI, isConnectorAPI } from '../types/state.types';
import { normalizeFieldType } from './normalizeFieldType';
import {
  TextIcon,
  IntegerIcon,
  DecimalIcon,
  FractionIcon,
  BooleanIcon,
  RangeIcon,
  MultiChoiceIcon,
  ConnectorIcon,
  CategoryIcon,
  ButtonWidgetIcon,
  ContentBlockIcon,
  BuildIcon,
  DividerIcon,
} from 'assests/GmsIcons';

/**
 * The ONE render site for every item-type glyph, and therefore the one place the
 * dark-mode re-ink lives (okf/isr/e2e.md, dark-mode migration — 2026-09-09 note).
 *
 * GmsIcons bake their paints (`stroke="#172B4D"`, `fill="black"`, `#6D788C`…), so a
 * bare glyph is invisible on the dark ladder — the MultiChoice list icon beside "F1"
 * in the config-panel header was the user's screenshot. This factory returned bare
 * elements and none of its three consumers (ConfigPanel's header, the two container
 * trees) wrapped them; the `iconInk.audit` scan never saw it either, because it walked
 * `.tsx` files for `<XxxIcon` literals and this is `React.createElement` in a `.ts`.
 * Both gaps are closed: the wrapper is here, and the audit now scans both shapes.
 *
 * `icon-follow-ink` (src/index.css) repaints every stroked/filled shape in
 * `currentColor` while preserving the light interior chips (white, #F4F5F7), so the
 * glyph takes whatever ink its slot names — `ink-secondary` in all three consumers.
 */
const inked = (Icon: React.FC): React.ReactElement =>
  React.createElement(
    'span',
    { className: 'icon-follow-ink tw-inline-flex tw-items-center tw-justify-center tw-shrink-0' },
    React.createElement(Icon),
  );

/** Get icon based on item type — always inside the re-ink wrapper. */
export const getItemIcon = (item: ItemAPI): React.ReactNode => {
  if (isConnectorAPI(item)) return inked(ConnectorIcon);
  if (isFieldAPI(item)) {
    const normalizedType = normalizeFieldType(item.fieldType);
    switch (normalizedType) {
      case 'text': return inked(TextIcon);
      case 'integer': return inked(IntegerIcon);
      case 'decimal': return inked(DecimalIcon);
      case 'fraction': return inked(FractionIcon);
      case 'boolean': return inked(BooleanIcon);
      case 'range': return inked(RangeIcon);
      case 'multichoice': return inked(MultiChoiceIcon);
      default: return inked(TextIcon);
    }
  }
  if (isWidgetAPI(item)) {
    switch (item.widgetType) {
      case 'button': return inked(ButtonWidgetIcon);
      case 'contentBlock': return inked(ContentBlockIcon);
      case 'yourBuild': return inked(BuildIcon);
      case 'divider': return inked(DividerIcon);
      case 'build': return inked(BuildIcon);
      default: return inked(ButtonWidgetIcon);
    }
  }
  if (isCategoryAPI(item)) return inked(CategoryIcon);
  return null;
};

/** Get display name for an item */
export const getItemName = (item: ItemAPI): string => {
  if ('name' in item && item.name) return item.name;
  if (isFieldAPI(item)) return item.fieldType;
  if (isWidgetAPI(item)) return item.widgetType;
  return 'Unnamed';
};

/** Get type label for display (badge / parenthetical) */
export const getItemTypeName = (item: ItemAPI): string => {
  if (isConnectorAPI(item)) return 'Connector';
  if (isFieldAPI(item)) return item.fieldType;
  if (isWidgetAPI(item)) return 'Widget';
  if (isCategoryAPI(item)) return 'Category';
  return 'Item';
};

/** Truncate a name to maxLength characters */
export const truncateName = (name: string, maxLength: number = 25): string => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
};
